import { useMemo, useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import { isBitSet, longDateString } from "../../utils";
import "../../starek.css";

const DAYS_OF_WEEK = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
];

const formatTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

const runsOnToDays = (mask) => {
    const days = [];
    DAYS_OF_WEEK.forEach((day, i) => {
        if (isBitSet(mask, i)) days.push(day.slice(0, 3));
    });
    return days.length ? days.join(", ") : "-";
};

function ShiftListingView({
    items,
    selectedRows,
    onSelectedRowsChange,
    filterText,
    toggleCleared,
}) {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const filteredItems = useMemo(() => {
        const q = filterText?.trim().toLowerCase();
        if (!q) return items;
        return items.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q),
        );
    }, [items, filterText]);

    const paginatedItems = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredItems.slice(start, start + rowsPerPage);
    }, [filteredItems, page, rowsPerPage]);

    useEffect(() => {
        setPage(1);
    }, [filterText]);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePerRowsChange = (newPerPage, newPage) => {
        setRowsPerPage(newPerPage);
        setPage(newPage);
    };

    const handleRowSelection = useCallback(
        ({ selectedRows: currentPageSelected }) => {
            const visibleIds = new Set(paginatedItems.map((r) => r.id));
            const otherSelected = selectedRows.filter(
                (r) => !visibleIds.has(r.id),
            );
            const newSelection = [...otherSelected, ...currentPageSelected];
            const currentIds = new Set(selectedRows.map((r) => r.id));
            const newIds = new Set(newSelection.map((r) => r.id));

            if (
                currentIds.size === newIds.size &&
                [...currentIds].every((id) => newIds.has(id))
            ) {
                return;
            }

            onSelectedRowsChange({ selectedRows: newSelection });
        },
        [paginatedItems, selectedRows, onSelectedRowsChange],
    );

    const isRowSelected = useCallback(
        (row) => selectedRows.some((r) => r.id === row.id),
        [selectedRows],
    );

    const columns = useMemo(
        () => [
            {
                name: "Name",
                selector: (r) => r.name || "-",
                sortable: true,
                height: "50px",
                grow: 1.2,
            },
            {
                name: "Description",
                selector: (r) => r.description || "-",
                sortable: true,
                height: "50px",
                grow: 2,
                cell: (r) => (
                    <div
                        className="text-truncate h-50px"
                        style={{ maxWidth: 250 }}
                    >
                        {r.description || "-"}
                    </div>
                ),
            },
            {
                name: "Timings",
                selector: (r) =>
                    `${formatTime(r.start_time)} - ${formatTime(r.end_time)}`,
                height: "50px",
                width: "180px",
                sortable: true,
            },
            {
                name: "Days",
                selector: (r) => runsOnToDays(r.runs_on),
                height: "50px",
                grow: 1,
            },
            {
                name: "Activity",
                selector: (r) => r.updated_at,
                height: "50px",
                width: "250px",
                sortable: true,
                cell: (r) => {
                    const tooltipText = `Created by ${
                        r.created_by?.name || "Unknown"
                    } at ${
                        r.created_at ? longDateString(r.created_at) : "-"
                    }\nModified by ${r.updated_by?.name || "Unknown"} at ${
                        r.updated_at ? longDateString(r.updated_at) : "-"
                    }`;
                    return (
                        <div
                            style={{ fontSize: "0.85rem" }}
                            title={tooltipText}
                        >
                            {r.updated_by?.name || "Unknown"} at{" "}
                            {r.updated_at ? longDateString(r.updated_at) : "-"}
                        </div>
                    );
                },
            },
        ],
        [],
    );

    return (
        <DataTable
            columns={columns}
            data={paginatedItems}
            selectableRows
            onSelectedRowsChange={handleRowSelection}
            selectableRowSelected={isRowSelected}
            clearSelectedRows={toggleCleared}
            highlightOnHover
            striped
            pointerOnHover
            responsive
            pagination
            paginationServer
            paginationTotalRows={filteredItems.length}
            paginationDefaultPage={page}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            dense
            selectableRowsHighlight
            selectableRowsNoSelectAll={false}
        />
    );
}

export default ShiftListingView;
